import { useCallback, useMemo, useState } from 'react';
import {
  ContextMenu,
  ContextMenuBuilder,
  useInputTableActions,
  usePasteConfig,
  type ButtonType,
  type RepeatRowsPayload
} from '@/components/custom/erp/input-table-v2';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { useAutoSelectBoPhan } from './hooks/useAutoSelectBoPhan';
import type { AccountConstraint, VatTu } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import type { SelectedCellInfo } from '../hooks';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  ck_yn: boolean;
  loai_ck?: string;
  onRowClick?: DataTableRowEventHandler<BanHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<BanHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onAllocateDiscount: (mode: 'quantity' | 'value') => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BanHangRecord>) => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  onVatTuSelection?: (rowUuid: string, rows: VatTu[]) => void | Promise<void>;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
  constraints?: AccountConstraint[];
}

const DETAIL_ACTION_ORDER: ButtonType[] = [
  'add',
  'delete',
  'copy',
  'paste',
  'moveUp',
  'moveDown',
  'export',
  'pin',
  'viewInventory',
  'viewReceipt'
];

const detail = (row: BanHangRecord) => ({
  ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
  ma_kho: row.ma_kho_data?.uuid || row.ma_kho || '',
  so_luong: row.so_luong,
  ...constraintValues(row)
});

const detailRowId = (row: BanHangRecord) => row?.uuid;

export function DetailTab({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  ck_yn,
  loai_ck,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onAllocateDiscount,
  onRepeatForRows,
  onCellValueChange,
  onVatTuSelection,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting,
  constraints = []
}: DetailTabProps) {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: BanHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const handleCellValueChange = useAutoSelectBoPhan(onCellValueChange);

  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });

  const columns = useMemo(
    () => getDetailTableColumns(formMode, ck_yn, handleCellValueChange, onVatTuSelection, cellError) as BanHangRecord[],
    [ck_yn, formMode, handleCellValueChange, onVatTuSelection, cellError]
  );

  usePasteConfig({
    columns,
    onNonWritableFields: handleUpdateNonWritableFields,
    onPasteConfigs: handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: detailRowId,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );

  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: BanHangTableParams) => {
      onCellClick?.(params);
      const pointerEvent = event as MouseEvent;

      setContextMenu({
        position: { x: pointerEvent.clientX, y: pointerEvent.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );

  const tableActions = useInputTableActions({
    onAddRow,
    onCopyRow,
    onDeleteRow,
    onMoveRow,
    onPasteRow
  });

  const actionMenu = useMemo(
    () =>
      loai_ck === '3'
        ? {
            label: 'Phân bổ chiết khấu',
            icon: 139,
            items: [
              {
                key: 'quantity',
                label: 'Theo số lượng',
                onSelect: () => onAllocateDiscount('quantity')
              },
              {
                key: 'value',
                label: 'Theo giá trị',
                onSelect: () => onAllocateDiscount('value')
              }
            ]
          }
        : undefined,
    [loai_ck, onAllocateDiscount]
  );

  return (
    <>
      <ErpInputTableV2
        data-perf-id='hdbh-form-detail-table'
        data-row-count={rows.length}
        rows={rows}
        autoHeight
        showIndexColumn
        forceVirtualization
        virtualizationOverscan={3}
        virtualizationInitialRows={24}
        rowHeight={30}
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick as BanHangRecord}
        onCellClick={onCellClick as BanHangRecord}
        onCellContextMenu={handleCellContextMenu}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns as BanHangRecord[]}
        getRowId={detailRowId}
        pasteTarget={selectedCell}
        actionLoading={isPasting}
        actionsDisabled={formMode === 'view'}
        actionOrder={DETAIL_ACTION_ORDER}
        actions={tableActions}
        actionMenu={actionMenu}
      />

      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
}
